import type { AuctionEventStatus } from '@neon-one/auctions-types';

import type { PrototypeAuctionStatus } from '@/shell/prototype-shim';

/**
 * One landing map for every "where does this auction open" decision
 * (GT1-459): draft/scheduled -> Catalog, live -> Console, closed -> the
 * settlement Console.
 *
 * `closed` is terminal, so the Console is the only post-close destination —
 * there is no second status to split on. `'reconcile-report'` stays in the
 * union because Reports & Reconciliation is a real route (the sidenav links to
 * it unconditionally from BuilderShell), but nothing lands there.
 *
 * Accepts wire statuses and the UI's prototype buckets so index components
 * (prototype statuses) and router guards (wire statuses) share one source of
 * truth.
 */
type AnyStatus = AuctionEventStatus | PrototypeAuctionStatus;

type LandingRouteName =
  | 'catalog-overview'
  | 'run-live'
  | 'reconcile-report'
  | 'settlement-console';

function toWire(s: AnyStatus): AuctionEventStatus {
  if (s === 'scheduled') return 'published';
  if (s === 'live') return 'open';
  return s;
}

export function landingRouteNameFor(status: AnyStatus): LandingRouteName {
  const s = toWire(status);
  if (s === 'open') return 'run-live';
  if (s === 'closed') return 'settlement-console';
  return 'catalog-overview';
}

// Route names, not path segments — settlement-console lives at
// /auctions/:id/console, which is why this table exists.
const LANDING_PATH_SEGMENT: Record<LandingRouteName, string> = {
  'catalog-overview': 'catalog',
  'run-live': 'run-live',
  'reconcile-report': 'reconcile-report',
  'settlement-console': 'console',
};

export function landingPathFor(auctionId: string, status: AnyStatus): string {
  // Encoded because vue-router hands `params.id` over already decoded: an id
  // holding %2F would otherwise rebuild into a path with a real slash, landing
  // on a different route than the one asked for.
  return `/auctions/${encodeURIComponent(auctionId)}/${LANDING_PATH_SEGMENT[landingRouteNameFor(status)]}`;
}

export function landingCtaLabelFor(status: AnyStatus): string {
  const s = toWire(status);
  if (s === 'draft') return 'Resume';
  if (s === 'published') return 'Open';
  if (s === 'open') return 'Manage auction';
  // Closed is terminal and the Console is where it opens; the label names that
  // destination rather than implying work is still pending on an auction with
  // nothing left to collect (GT1-460 AC#7/#8).
  return 'View Console';
}
